// Raspberry Pi Pico en 3D (para protoboard): PCB verde de 51 × 21 mm con pines almenados y tiras de pines,
// micro-USB, botón BOOTSEL, LED de GP25, RP2040, flash, cristal, regulador y los 3 pines de depuración (SWD).
// En la Pico real los nombres de los pines van por abajo; aquí se dibujan arriba para poder leerlos.
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape, glow, setGlow, emissiveMat, setEmissive } from '../kit.js';
import { PICO } from '../../parts/pico.js';
import { getPins } from '../../parts/registry.js';
import { rand } from './uno.js';

const W = PICO.W;
const D = PICO.D;
const TOP = PICO.PCB_TOP;
const GREEN = '#17803d';
const SILK = '#f4f6f2';

// Posiciones (mm, centro de la placa, USB a la izquierda) tomadas de fotos de la Pico Rev3.
const LAYOUT = {
  usb: { x: -23.3, z: 0, w: 5.7, d: 7.6, h: 2.7, over: 1.3 },
  bootsel: { x: -13.6, z: -3.6 },
  led: { x: -21.6, z: 5.4 },
  rp2040: { x: 1.2, z: 0.4, s: 7 },
  flash: { x: -6.6, z: -4.4, w: 3, d: 2 },
  xtal: { x: 8.6, z: 4.4 },
  smps: { x: -19.4, z: -5.6 }, // regulador RT6150 y su bobina, junto a VBUS y VSYS
  debug: { x: W / 2 - 1.3, zs: [-2.54, 0, 2.54] },
};

let silk = null;

function drawSilk(pins) {
  const { c, g } = canvas(W, D, 26);
  const X = (x) => x + W / 2;
  const Y = (z) => z + D / 2;
  g.fillStyle = GREEN;
  g.fillRect(0, 0, W, D);
  // Pistas bajo la máscara.
  const r = rand(2040);
  g.strokeStyle = 'rgba(8,70,30,0.55)';
  g.lineWidth = 0.18;
  g.lineCap = 'round';
  for (let i = 0; i < 46; i++) {
    let x = 3 + r() * (W - 6);
    let y = 3.5 + r() * (D - 7);
    g.beginPath();
    g.moveTo(x, y);
    for (let k = 0; k < 3; k++) {
      if (r() > 0.5) x = Math.max(2.5, Math.min(W - 2.5, x + (r() - 0.5) * 14));
      else y = Math.max(3.4, Math.min(D - 3.4, y + (r() - 0.5) * 8));
      g.lineTo(x, y);
    }
    g.stroke();
  }
  g.fillStyle = 'rgba(220,235,220,0.5)';
  for (let i = 0; i < 30; i++) {
    g.beginPath();
    g.arc(4 + r() * (W - 8), 4 + r() * (D - 8), 0.22, 0, Math.PI * 2);
    g.fill();
  }
  // Almenas: medio agujero dorado en el borde y el agujero pasante del pin.
  for (const p of pins) {
    const edge = p.z > 0 ? D : 0;
    g.fillStyle = '#d9b25a';
    g.fillRect(X(p.x) - 0.8, edge === 0 ? 0 : D - 2.6, 1.6, 2.6);
    g.beginPath();
    g.arc(X(p.x), Y(p.z), 0.85, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#121214';
    g.beginPath();
    g.arc(X(p.x), Y(p.z), 0.5, 0, Math.PI * 2);
    g.fill();
    g.beginPath();
    g.arc(X(p.x), edge, 0.5, 0, Math.PI * 2);
    g.fill();
    // Nombre del pin hacia el centro de la placa.
    const name = p.name.replace(/\.\d+$/, '').replace('ADC_VREF', 'VREF').replace('3V3_EN', '3V3E');
    text(g, name, X(p.x), Y(p.z) + (p.z > 0 ? -1.75 : 1.75), { size: 0.62, color: SILK, weight: 700, rotate: -Math.PI / 2 });
  }
  // Pines de depuración (SWD) en el extremo derecho.
  const dbg = LAYOUT.debug;
  for (const z of dbg.zs) {
    g.fillStyle = '#d9b25a';
    g.fillRect(W - 2.2, Y(z) - 0.75, 2.2, 1.5);
    g.fillStyle = '#121214';
    g.beginPath();
    g.arc(X(dbg.x), Y(z), 0.42, 0, Math.PI * 2);
    g.fill();
  }
  text(g, 'DEBUG', X(dbg.x - 2.3), Y(0), { size: 0.7, color: SILK, weight: 700, rotate: -Math.PI / 2 });
  // Números de los pines de las esquinas y leyendas.
  text(g, '1', X(-24.13) + 1.6, Y(PICO.ROW_Z) - 2.9, { size: 0.75, color: SILK, weight: 700 });
  text(g, '40', X(-24.13) + 1.8, Y(-PICO.ROW_Z) + 2.9, { size: 0.75, color: SILK, weight: 700 });
  text(g, '20', X(24.13) - 1.9, Y(PICO.ROW_Z) - 2.9, { size: 0.75, color: SILK, weight: 700 });
  text(g, '21', X(24.13) - 1.9, Y(-PICO.ROW_Z) + 2.9, { size: 0.75, color: SILK, weight: 700 });
  text(g, 'BOOTSEL', X(LAYOUT.bootsel.x), Y(LAYOUT.bootsel.z) + 3.1, { size: 0.75, color: SILK, weight: 700 });
  text(g, 'LED', X(LAYOUT.led.x) + 2.6, Y(LAYOUT.led.z), { size: 0.75, color: SILK, weight: 700 });
  text(g, 'Raspberry Pi Pico', X(15.5), Y(-1.4), { size: 1.35, color: SILK, weight: 700 });
  text(g, '©2020', X(15.5), Y(0.7), { size: 1.0, color: SILK, weight: 600 });
  // Frambuesa estilizada.
  const lx = X(15.5);
  const ly = Y(3.6);
  g.strokeStyle = SILK;
  g.lineWidth = 0.18;
  for (const [dx, dy] of [[0, 0.9], [-0.75, 0.25], [0.75, 0.25], [-0.4, -0.45], [0.4, -0.45], [0, 1.65]]) {
    g.beginPath();
    g.arc(lx + dx, ly + dy - 0.4, 0.42, 0, Math.PI * 2);
    g.stroke();
  }
  const t = texture(c);
  t.userData.shared = true;
  return t;
}

let chipTex = null;

function rpLabel() {
  const s = LAYOUT.rp2040.s;
  const { c, g } = canvas(s, s, 40);
  g.fillStyle = '#1b1c1e';
  g.fillRect(0, 0, s, s);
  text(g, 'RP2-B2', s / 2, s / 2 - 0.9, { size: 0.95, color: '#a9abb0', weight: 700 });
  text(g, '20/21', s / 2, s / 2 + 0.4, { size: 0.7, color: '#8a8c91', weight: 600 });
  text(g, 'P64M15.00', s / 2, s / 2 + 1.5, { size: 0.6, color: '#8a8c91', weight: 600 });
  g.fillStyle = '#0f1011';
  g.beginPath();
  g.arc(0.9, 0.9, 0.35, 0, Math.PI * 2);
  g.fill();
  const t = texture(c);
  t.userData.shared = true;
  return t;
}

export function buildPico(def, props) {
  const pins = getPins(def.type, props);
  if (!silk) silk = drawSilk(pins);
  if (!chipTex) chipTex = rpLabel();
  const b = new Builder();
  const pcb = extrudeBoard(boardShape(W, D, { r: 0.6 }), PICO.PCB_T, silk, '#126b33', { w: W, d: D, y0: PICO.PCB_Y });
  b.root.add(pcb);

  // Tiras de pines macho bajo la placa: separador negro y patas doradas que bajan a la protoboard.
  for (const z of [-PICO.ROW_Z, PICO.ROW_Z]) b.box(20 * 2.54, PICO.PCB_Y - 0.1, 2.54, M.blackPlastic, 0, 0.1, z);
  for (const p of pins) {
    b.box(0.64, TOP + 0.7, 0.64, M.gold, p.x, 0, p.z);
    b.cyl(0.8, 0.8, 0.12, M.tin, p.x, TOP, p.z, 10);
  }
  // Pines de depuración: almenas sin tira.
  for (const z of LAYOUT.debug.zs) b.cyl(0.7, 0.7, 0.1, M.gold, LAYOUT.debug.x, TOP, z, 10);

  // Micro-USB (sobresale del borde izquierdo).
  const u = LAYOUT.usb;
  b.box(u.w, u.h, u.d, M.metal, u.x, TOP, u.z);
  b.box(0.2, 1.3, 5.2, M.hole, u.x - u.w / 2 - 0.05, TOP + 0.7, u.z);
  b.box(1.2, 0.5, 3.8, mat('#2a2b2e', { roughness: 0.5 }), u.x - u.w / 2 + 0.7, TOP + 1.1, u.z);
  // RP2040 (QFN 7 × 7 mm), flash, cristal, regulador con su bobina y capacitores.
  const rp = LAYOUT.rp2040;
  b.box(rp.s, 0.85, rp.s, M.chip, rp.x, TOP, rp.z);
  const lbl = new THREE.Mesh(new THREE.PlaneGeometry(rp.s, rp.s).rotateX(-Math.PI / 2), new THREE.MeshStandardMaterial({ map: chipTex, roughness: 0.6 }));
  lbl.position.set(rp.x, TOP + 0.86, rp.z);
  lbl.receiveShadow = true;
  b.root.add(lbl);
  const f = LAYOUT.flash;
  b.box(f.w, 0.55, f.d, M.chip, f.x, TOP, f.z);
  b.rbox(3.2, 0.8, 2.5, 0.3, M.metal, LAYOUT.xtal.x, TOP, LAYOUT.xtal.z);
  const s = LAYOUT.smps;
  b.box(3, 1.5, 3, mat('#3a3b3f', { roughness: 0.75 }), s.x, TOP, s.z);
  b.box(2, 0.6, 2, M.chip, s.x + 3.2, TOP, s.z - 0.6);
  const r = rand(77);
  const caps = [mat('#b8a07a', { roughness: 0.6 }), mat('#8d7a5c', { roughness: 0.6 }), mat('#2b2722', { roughness: 0.5 })];
  for (let i = 0; i < 22; i++) {
    const x = -10 + r() * 22;
    const z = -6 + r() * 12;
    if (Math.abs(x - rp.x) < 4.6 && Math.abs(z - rp.z) < 4.6) continue;
    b.box(1.0, 0.5, 0.5, caps[i % 3], x, TOP, z, { ry: r() > 0.5 ? Math.PI / 2 : 0 });
  }
  // BOOTSEL: base blanca y botón que se puede presionar.
  const bs = LAYOUT.bootsel;
  b.box(4.2, 1.6, 3.4, mat('#e9e8e2', { roughness: 0.55 }), bs.x, TOP, bs.z);
  b.build();
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 0.85, 0.7, 18), mat('#f7f6f1', { roughness: 0.45 }));
  cap.position.set(bs.x, TOP + 1.95, bs.z);
  cap.castShadow = true;
  cap.userData.interactive = { kind: 'press', key: 'bootsel', label: 'BOOTSEL' };
  b.root.add(cap);

  // LED verde de GP25.
  const ledMat = emissiveMat('#f2efe6', '#3dff5a', { roughness: 0.3 });
  const led = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.6, 0.8), ledMat);
  led.position.set(LAYOUT.led.x, TOP + 0.3, LAYOUT.led.z);
  b.root.add(led);
  const halo = glow('#3dff5a', 7);
  halo.position.set(LAYOUT.led.x, TOP + 1.2, LAYOUT.led.z);
  b.root.add(halo);

  const view = {
    root: b.root,
    update(v) {
      const on = v && !v.off ? v.L || 0 : 0;
      setEmissive(ledMat, on, 14);
      ledMat.color.set(on > 0.05 ? '#7dff8a' : '#f2efe6');
      setGlow(halo, on * 0.85);
    },
    reset() {
      view.update({});
    },
    press(key, down) {
      if (key === 'bootsel') cap.position.y = TOP + (down ? 1.75 : 1.95);
    },
  };
  view.reset();
  return view;
}
